import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage74() {
  const contentRef = useRef(null);
  const titleText = "Bitcoin’s Scripting Language, OP_CODES and Scripts";
  const links = {
    prev: "/bitcoin-tech-page-73",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-75",
  };
  const questions = [
    {
      text: "1. Why is Bitcoin Script described as Turing-incomplete?",
      options: [
        {
          value: "A",
          label: "A. It does not support cryptographic operations",
        },
        {
          value: "B",
          label:
            "B. It lacks loops and unbounded recursion, ensuring deterministic execution",
        },
        {
          value: "C",
          label: "C. It can only validate arithmetic expressions",
        },
        {
          value: "D",
          label: "D. It requires off-chain computation to function",
        },
      ],
      correct: "B",
    },
    {
      text: "2. How does Bitcoin determine whether a script execution is valid?",
      options: [
        {
          value: "A",
          label: "A. When all OP_CODES execute without errors",
        },
        {
          value: "B",
          label: "B. When miners approve the transaction",
        },
        {
          value: "C",
          label: "C. When the final stack contains a nonzero (true) value",
        },
        {
          value: "D",
          label: "D. When OP_CHECKSIG is present in the script",
        },
      ],
      correct: "C",
    },
    {
      text: "3. In a Pay-to-Public-Key-Hash (P2PKH) transaction, what is the purpose of OP_EQUALVERIFY?",
      options: [
        {
          value: "A",
          label: "A. To verify the transaction fee",
        },
        {
          value: "B",
          label:
            "B. To ensure the provided public key hash matches the expected hash",
        },
        {
          value: "C",
          label: "C. To validate the digital signature",
        },
        {
          value: "D",
          label: "D. To check the number of required signatures",
        },
      ],
      correct: "B",
    },
    {
      text: "4. What is a key advantage of P2PKH compared to the older P2PK transaction type?",
      options: [
        {
          value: "A",
          label: "A. It allows complex loops in scripts",
        },
        {
          value: "B",
          label:
            "B. It hides the public key until the output is spent, improving privacy",
        },
        {
          value: "C",
          label: "C. It removes the need for digital signatures",
        },
        {
          value: "D",
          label: "D. It enables automatic transaction reversal",
        },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  const [copied, setCopied] = useState(false);

  const script = "2 3 OP_ADD 5 OP_EQUAL";
  const P2PK_LOCKINGSCRIPT = "<PubKey> OP_CHECKSIG";
  const P2PK_SCRITPSIG = "<Signature>";
  const P2PKH_LOCKING_SCRIPT =
    "OP_DUP OP_HASH160 <PublicKeyHash> OP_EQUALVERIFY OP_CHECKSIG";
  const P2PKH_SCRIPTSIG = "<Signature> <PublicKey>";
  const P2PKH_STEP1 = "Stack: [Signature, PublicKey]";
  const P2PKH_STEP2 = "Stack: [Signature, PublicKey, PublicKey]";
  const P2PKH_STEP3 = "Stack: [Signature, PublicKey, PublicKeyHash]";
  const P2PKH_STEP4 =
    "Stack: [Signature, PublicKey, PublicKeyHash, ExpectedPublicKeyHash]";
  const P2PKH_STEP5 = " Stack: [Signature, PublicKey]";
  const P2PKH_STEP6 = "Stack: [True]";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={74}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 74</b>! Today, we begin exploring Bitcoin scripts and
        opcodes, the simple yet powerful building blocks that define how Bitcoin
        transactions are validated and spent. This lesson introduces how scripts
        work, why Bitcoin uses a limited, non-Turing-complete scripting
        language, and how opcodes enforce the rules that keep the network secure
        and predictable.
        <br />
        <br /> Bitcoin is often referred to as "programmable money," but its
        scripting language, Bitcoin Script, is significantly different from the
        general-purpose programming languages used in other crypto-currecnies or
        smart contract platforms. Bitcoin Script is a stack-based, purposefully
        restricted language designed for validating transactions in a secure and
        deterministic manner.
        <br />
        Bitcoin Script operates in a straightforward manner using a stack-based
        approach. The execution process involves pushing values onto the stack,
        applying operations (opcodes), and verifying the final stack state. If
        the script executes successfully and leaves a valid output, the
        transaction is considered valid. Otherwise, it is rejected by the
        network. The simplicity of Bitcoin Script ensures that transactions
        remain deterministic, meaning they will always produce the same result
        given the same input.
        <br />
        <br /> <b>Understanding OP_CODES in Bitcoin Script</b>
        <br />
        <br />
        Before digging further into how Bitcoin scripts operate, we must first
        understand OP_CODES (Operation Codes). <br />
        Bitcoin Script is a stack-based, Turing-incomplete language that follows
        a strict set of rules for transaction verification. Unlike
        general-purpose programming languages, Bitcoin Script does not allow
        loops or complex computations, ensuring that all scripts execute in a
        deterministic and efficient manner.
        <br /> OP_CODES are the fundamental instructions that Bitcoin Script
        uses to manipulate data on the stack. These small, predefined operations
        enable Bitcoin transactions to be verified securely without requiring a
        central authority. OP_CODES can perform mathematical operations,
        cryptographic verifications, logical checks, and conditional flows.
        <br />
        <br />
        Bitcoin Script OP_CODES can be categorized into several types based on
        their function:
        <br />
        <br />
        1. Stack Manipulation OP_CODES
        <br />
        <br />
        <table className="trade-table">
          <thead>
            <tr>
              <th>OP_CODE</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>OP_DUP</code>
              </td>
              <td>Duplicates the top item on the stack.</td>
            </tr>
            <tr>
              <td>
                <code>OP_DROP</code>
              </td>
              <td>Removes the top item from the stack.</td>
            </tr>
            <tr>
              <td>
                <code>OP_SWAP</code>
              </td>
              <td>Swaps the positions of the top two stack items.</td>
            </tr>
          </tbody>
        </table>
        <br />
        <br />
        2. Arithmetic and Logic OP_CODES
        <br />
        <br />
        <table className="trade-table">
          <thead>
            <tr>
              <th>OP_CODE</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>OP_ADD</code>
              </td>
              <td>Adds two numbers.</td>
            </tr>
            <tr>
              <td>
                <code>OP_SUB</code>
              </td>
              <td>Subtracts one number from another.</td>
            </tr>
            <tr>
              <td>
                <code>OP_EQUAL</code>
              </td>
              <td>Checks if two values are equal.</td>
            </tr>
            <tr>
              <td>
                <code>OP_EQUALVERIFY</code>
              </td>
              <td>Verifies equality and halts execution if false.</td>
            </tr>
          </tbody>
        </table>
        <br />
        <br />
        3. Cryptographic OP_CODES <br />
        <br />
        <table className="trade-table">
          <thead>
            <tr>
              <th>OP_CODE</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>OP_HASH160</code>
              </td>
              <td>
                Applies SHA-256 followed by RIPEMD-160 (used in P2PKH
                transactions).
              </td>
            </tr>
            <tr>
              <td>
                <code>OP_SHA256</code>
              </td>
              <td>Computes the SHA-256 hash of the top stack item.</td>
            </tr>
            <tr>
              <td>
                <code>OP_CHECKSIG</code>
              </td>
              <td>Verifies a digital signature against a public key.</td>
            </tr>
            <tr>
              <td>
                <code>OP_CHECKMULTISIG</code>
              </td>
              <td>
                Verifies multiple signatures for multi-signature transactions.
              </td>
            </tr>
          </tbody>
        </table>
        <br />
        <br />
        4. Conditional Execution OP_CODES
        <br />
        <br />
        <table className="trade-table">
          <thead>
            <tr>
              <th>OP_CODE</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>OP_IF / OP_ELSE / OP_ENDIF</code>
              </td>
              <td>Implements conditional branching.</td>
            </tr>
            <tr>
              <td>
                <code>OP_VERIFY</code>
              </td>
              <td>
                Checks if the top stack value is true; if not, the script fails.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="content">
          <b>Bitcoin Locking and Unlocking Scripts</b>
          <br />
          <br />
          In Bitcoin, a scriptPubKey (locking script) and scriptSig (unlocking
          script) work together to define and satisfy spending conditions for a
          transaction output. The scriptPubKey is embedded in a transaction
          output and specifies the rules that must be met to spend the funds.{" "}
          <br />
          It typically contains a public key (or its hash) and opcodes that
          enforce verification, such as OP_CHECKSIG for validating digital
          signatures. When a user wants to spend a UTXO (unspent transaction
          output), they provide a scriptSig in the input of a new transaction.
          The scriptSig contains the necessary data (such as a signature or
          public key) to satisfy the conditions set in the scriptPubKey.
          <br /> When a transaction is validated, Bitcoin nodes execute the
          scriptSig first, followed by the scriptPubKey, treating them as a
          single combined script. If the final stack evaluation results in true,
          the spending conditions are met, and the transaction is considered
          valid. This mechanism ensures that only authorized users can spend
          Bitcoin, enabling features like multi-signature security and
          time-locked transactions.
        </p>
      </p>
      <p className="content">
        <b>
          Why Bitcoin Script is Turing-Incomplete and How It Determines
          Transaction Validity
        </b>
        <br />
        <br />
        Bitcoin Script is a stack-based, Turing-incomplete programming language
        designed specifically for transaction validation. It is
        Turing-incomplete because it lacks loops and unbounded recursion,
        meaning it cannot perform arbitrary computations or enter infinite
        loops—this ensures that all scripts execute in a predictable, finite
        amount of time, preventing denial-of-service (DoS) attacks. Instead of
        traditional control flow, Bitcoin Script operates through a stack, where
        data is pushed and manipulated using OP_CODES. The success or failure of
        a script is determined by the final state of the stack after execution.
        If, at the end of execution, the top item on the stack is a nonzero
        (true) value, the script is considered valid, and the transaction is
        accepted. However, if the stack is empty or the top value is zero
        (false), the script fails, and the transaction is rejected. This
        deterministic, straightforward execution model ensures Bitcoin
        transactions can be securely verified by all network participants
        without ambiguity.
      </p>
      <h2>Basic Addition Script Execution Example:</h2>
      <p className="content">
        Bitcoin Script follows a stack-based model where data is pushed onto the
        stack, manipulated by opcodes, and ultimately evaluated for correctness.
        Execution proceeds in a last-in, first-out (LIFO) manner, ensuring a
        predictable flow of operations.
        <br />
        To demonstrate how Bitcoin's stack-based system works, consider a simple
        script that adds two numbers:
      </p>
      <div className="code-block">
        <pre>{script}</pre>
      </div>
      <p className="content">
        <br />
        <br />
        The above script executes as follows: <br />
        <br />
        1. Push 2 onto the stack. <br />
        2. Push 3 onto the stack.
        <br />
        3. Apply OP_ADD, which pops 2 and 3, adds them, and pushes 5 onto the
        stack. <br />
        4. Push 5 onto the stack (expected value). <br />
        5. Apply OP_EQUAL, which checks if the two topmost values (5 and 5) are
        equal. <br />
        6. Since they match, the script succeeds.
        <br />
        This simple example demonstrates how Bitcoin Script processes values
        deterministically, making it ideal for secure financial transactions.
      </p>
      <h2>Pay-to-Public-key(P2PK)</h2>
      <p className="content">
        Pay-to-Public-Key (P2PK) is one of the simplest Bitcoin script types,
        where the locking script (scriptPubKey) requires a valid signature
        corresponding to a specific public key to unlock and spend the funds.
        This type of transaction was commonly used in early Bitcoin but has
        since been largely replaced by Pay-to-Public-Key-Hash (P2PKH) due to
        improved security and privacy. <br />
        In a P2PK transaction, the scriptPubKey contains the recipient’s public
        key and an OP_CHECKSIG opcode, which verifies the spender’s provided
        signature against that public key. The unlocking script (scriptSig)
        consists of a single digital signature generated by the corresponding
        private key. <br />
        The execution proceeds as follows:
        <br /> first, the unlocking script pushes the signature onto the stack.
        Then, the locking script pushes the public key onto the stack and
        executes OP_CHECKSIG, which verifies that the provided signature is
        valid for the given public key. If the verification succeeds, the
        transaction is valid, and the funds can be spent.
        <br />
        <br />
        Example locking script (scriptPubKey):
      </p>
      <div className="code-block">
        <pre>{P2PK_LOCKINGSCRIPT}</pre>
      </div>
      <p className="content">
        Example unlocking script (scriptSig):
        <br />
      </p>
      <div className="code-block">
        <pre>{P2PK_SCRITPSIG}</pre>
      </div>
      <p className="content">
        Execution steps: <br />
        1. The unlocking script pushes Signature onto the stack.
        <br />
        2. The locking script pushes PubKey onto the stack.
        <br />
        3. OP_CHECKSIG checks if the Signature is valid for PubKey. <br />
        4.If the signature is correct, the script returns true, allowing the
        transaction to be confirmed. Otherwise, it fails.
      </p>
      <h2>Pay-to-Public-Key-Hash (P2PKH) Script Execution Example</h2>
      <p className="content">
        Pay-to-Public-Key-Hash (P2PKH) is the most widely used Bitcoin
        transaction type, designed to improve both privacy and efficiency
        compared to Pay-to-Public-Key (P2PK). Instead of locking funds to a full
        public key, P2PKH locks them to a hashed version of the public key,
        known as the public key hash (PKH). This means that the recipient's full
        public key is not revealed in the transaction output, making it harder
        for someone to link addresses to known public keys until a spend occurs.
        When a sender creates a P2PKH transaction, they include a locking script
        (scriptPubKey) that ensures only the rightful owner—who possesses the
        corresponding private key—can unlock and spend the funds. To spend the
        output, the recipient provides an unlocking script (scriptSig)
        containing a valid digital signature and the public key. The Bitcoin
        network verifies that the provided public key matches the hashed public
        key from the locking script and that the signature is valid for that
        public key before allowing the transaction to proceed.
        <br />
        The execution of a P2PKH transaction follows a strict step-by-step
        process enforced by Bitcoin Script. First, the unlocking script is
        executed, pushing the provided signature and public key onto the stack.
        The script then duplicates the public key using OP_DUP and applies
        OP_HASH160 to convert it into a RIPEMD-160 hash (after first applying
        SHA-256). This hashed public key is then compared with the expected hash
        stored in the locking script using OP_EQUALVERIFY, ensuring that only
        the correct public key can be used. Finally, OP_CHECKSIG is used to
        verify that the digital signature corresponds to the provided public
        key. If all these conditions are met, the transaction is deemed valid,
        and the funds are spent. This mechanism ensures that only the rightful
        owner of the private key can authorize transactions, making P2PKH a
        fundamental building block of Bitcoin’s security model.
        <br />
        When spending a P2PKH output, the spender must provide both:
        <br />
        1. A digital signature proving ownership of the funds <br />
        2. The public key corresponding to the hashed public key in the script
        <br />
        <br />
        P2PKH Script Breakdown <br />
        <br />
        Locking Script (scriptPubKey): <br />A P2PKH script locks the output to
        the public key hash (PKH) instead of the full public key.
      </p>
      <div className="code-block">
        <pre>{P2PKH_LOCKING_SCRIPT}</pre>
      </div>
      <p className="content">
        Example unlocking script (scriptSig):
        <br /> The unlocking script is provided by the spender in the
        transaction input. Signature proves ownership of the private key.
        PublicKey is needed to compute the hash and compare it with
        PublicKeyHash.
        <br />
      </p>
      <div className="code-block">
        <pre>{P2PKH_SCRIPTSIG}</pre>
      </div>
      <p className="content">
        1. OP_DUP duplicates the public key provided in the unlocking script.
        <br />
        2. OP_HASH160 applies SHA-256 followed by RIPEMD-160 to the duplicated
        public key.
        <br />
        3. PublicKeyHash is the hash of the recipient’s public key.
        <br />
        4. OP_EQUALVERIFY ensures that the provided hash value matches the
        hashed public key of the recipient.
        <br />
        5. OP_CHECKSIG verifies the digital signature.
        <br />
        <br />
        Unlocking Script (scriptSig)
        <br />
      </p>

      <p className="content">
        <br /> <b>Step-by-Step Execution of a P2PKH Script:</b>
        <br />
        <br />
        <b>Step 1 </b>- Push Signature and Public Key onto the Stack
        <br />
        The unlocking script executes first:
      </p>
      <div className="code-block">
        <pre>{P2PKH_STEP1}</pre>
      </div>
      <p className="content">
        <b>Step 2</b>- Duplicate Public Key
        <br />
        OP_DUP duplicates the public key on top of the stack:
      </p>
      <div className="code-block">
        <pre>{P2PKH_STEP2}</pre>
      </div>
      <p className="content">
        <b>Step 3 </b> - Apply OP_HASH160
        <br />
        The top PublicKey is hashed (SHA-256 → RIPEMD-160):
      </p>
      <div className="code-block">
        <pre>{P2PKH_STEP3}</pre>
      </div>
      <p className="content">
        <b>Step 4</b> - Push Expected Public Key Hash
        <br />
        PublicKeyHash from the locking script is pushed onto the stack:
      </p>
      <div className="code-block">
        <pre>{P2PKH_STEP4}</pre>
      </div>
      <p className="content">
        <b>Step 5 </b> - Check Public Key Hash Matching
        <br /> OP_EQUALVERIFY ensures the computed hash matches the expected
        hash. If they match, both values are removed:
      </p>
      <div className="code-block">
        <pre>{P2PKH_STEP5}</pre>
      </div>
      <p className="content">
        <b>Step 6</b> - Verify Signature
        <br />
        OP_CHECKSIG verifies that the signature corresponds to the public key.
        If valid, the stack is reduced to TRUE (success):
      </p>
      <div className="code-block">
        <pre>{P2PKH_STEP6}</pre>
        {/* <button onClick={copyToClipboard} className="copy-button">
          <Copy size={18} />
        </button>
        {copied && <span className="copied-text">Copied!</span>} */}
      </div>
      <p className="content">
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today, we learned about Bitcoin Script and how it powers Bitcoin
        transactions through OP_CODES. We explored different types of OP_CODES,
        including stack manipulation, arithmetic & logic, cryptographic, and
        conditional execution,, and we organized them into easy-to-read tables
        for better understanding. We also took a deep dive into
        Pay-to-Public-Key (P2PK) and Pay-to-Script-Hash (P2SH) transactions,
        breaking down how their locking and unlocking scripts work. Along the
        way, we examined the roles of scriptPubKey and scriptSig, understanding
        how they define and satisfy spending conditions. Finally, we walked
        through a Bitcoin Script execution example, step by step, seeing how the
        stack processes values to validate a transaction. This journey gave us a
        solid foundation in Bitcoin's scripting language and how it secures
        financial transactions.
      </p>

      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage74;
